import { ReferrerCodeSchema } from "@biteasy/contracts";
import { z } from "zod";

export const createReferralLinkSchema = z.object({
	partnerId: z.string().min(1, "Partner is required"),
	slug: ReferrerCodeSchema.min(1, "Code is required"),
	payoutRateBps: z.coerce
		.number<number>()
		.int()
		.min(0, "Payout rate must be non-negative")
		.max(100, "Payout rate cannot exceed 100%"),
	/** Minimum payout in dollars — converted to cents before sending to server. */
	minPayoutDollars: z.coerce
		.number<number>()
		.min(25, "Minimum payout must be at least $25"),
	payoutDurationDays: z.coerce
		.number<number>()
		.int()
		.nullable()
		.transform((v) => (v === 0 ? null : v)),
	planHint: z.enum(["monthly", "yearly"]).nullable(),
	maxRedemptions: z.number().int().positive().nullable().default(null),
	expiresAt: z.string().nullable().default(null),
});

export const referralLinkDefaultsSchema = z.preprocess(
	(
		val: z.input<typeof createReferralLinkSchema> & { minPayoutCents?: number },
	) => {
		if (!val) return val;
		return {
			...val,
			planHint: val.planHint,
			payoutRateBps: val.payoutRateBps
				? Math.round(val.payoutRateBps / 100)
				: 0,
			// Server stores cents → form uses dollars
			minPayoutDollars:
				val.minPayoutCents != null
					? val.minPayoutCents / 100
					: (val.minPayoutDollars ?? 25),
			expiresAt: val.expiresAt ? new Date(val.expiresAt).toISOString() : null,
		};
	},
	createReferralLinkSchema,
);

export type CreateReferralLinkFormValues = z.input<
	typeof createReferralLinkSchema
> & {
	// Override unknowns from z.coerce/preprocess with form-friendly types
	payoutRateBps: string | number;
	minPayoutDollars: string | number;
	payoutDurationDays?: string | number | null;
};

export type CreateReferralLinkOutput = z.output<
	typeof createReferralLinkSchema
>;
